import { skipToken } from '@reduxjs/toolkit/query';
import { formatDate } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { isRequestArchived, RequestType } from '@strata/react-chat';
import { FormSection } from '@strata/react-form-section';
import { type ColumnDef, Grid, SecondaryData } from '@strata/react-grid';
import { MessagingBlock } from '@strata/react-messaging-block';
import { PayerRequestStatusIndicator } from '@strata/react-status-indicators';
import { useLocation, useNavigate } from 'react-router';

import { PayerRequestNumber } from './PayerRequestNumber';

import { type BackToDestination } from 'services/navigationHelpers';
import { AppRoutes } from 'services/routes';
import {
    type RequestDetailsSummary,
    useGetRequestsForTreatmentQuery,
} from 'store/api/payerRequestApi';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';

const initialSortProperties = [{ id: 'createDate', desc: true }];

const columns: ColumnDef<RequestDetailsSummary>[] = [
    {
        accessorKey: 'requestNumber',
        header: 'Request Number',
        size: 110,
        cell: ({ getValue, row }) => (
            <PayerRequestNumber text={getValue()} pbmOnly={row.original.pbmOnly} />
        ),
    },
    {
        accessorKey: 'requestSubType',
        header: 'Request Subtype',
        size: 110,
    },
    {
        accessorKey: 'createDate',
        header: 'Created On',
        size: 110,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'requestStatus',
        header: 'Status',
        size: 110,
        enableSorting: false,
        cell: ({ getValue }) => <PayerRequestStatusIndicator status={getValue()} />,
    },
];

interface ActiveRequestDetailsProps {
    payerId?: string;
    treatmentId?: string;
    memberId?: string;
    showRequests?: boolean;
    currentLocation: BackToDestination;
}

export const ActiveRequestDetails = ({
    payerId = undefined,
    treatmentId = undefined,
    memberId = undefined,
    showRequests = false,
    currentLocation,
}: ActiveRequestDetailsProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { data: treatmentRequests, isLoading: treatmentRequestsLoading } =
        useGetRequestsForTreatmentQuery(
            payerId && treatmentId && showRequests ? { payerId, treatmentId } : skipToken,
        );

    const { updateGridSearch: updateMemberInquiryGridSearch } = useGridState(Grids.ESCALATION);
    const {
        updateGridSearch: updateArchiveGridSearch,
        updateGridActiveFilters: updateArchiveGridActiveFilters,
    } = useGridState(Grids.ARCHIVE);

    return (
        <FormSection
            sectionTitle="Active Requests"
            content={
                <>
                    <Grid
                        data={treatmentRequests?.filter((request) => !isRequestArchived(request))}
                        isLoading={treatmentRequestsLoading}
                        sortProperties={initialSortProperties}
                        noResults={
                            <div className="padding__around--large">
                                <MessagingBlock
                                    size={MessagingBlock.Sizes.SMALL}
                                    primaryText="No active requests available"
                                />
                            </div>
                        }
                        onRowClick={(rowData) =>
                            navigate(AppRoutes.requestDetails(rowData.requestId), {
                                state: {
                                    backToDestination: {
                                        ...currentLocation,
                                        route: location.pathname,
                                    },
                                },
                            })
                        }
                        columns={columns}
                    />
                    <ActionButtonText
                        label="Search all active requests for member"
                        id="search-all-active-requests-button"
                        className="margin__top--medium"
                        onClick={() => {
                            if (memberId) {
                                // Preset the search and filters for the member inquiry grid
                                updateMemberInquiryGridSearch?.(memberId);
                                updateArchiveGridSearch?.(memberId);
                                updateArchiveGridActiveFilters?.({
                                    completed_date: ['ALL_TIME'],
                                });
                            }
                            navigate(
                                {
                                    pathname: AppRoutes.requests,
                                    hash: RequestType.ESCALATION.toLocaleLowerCase(),
                                },
                                {
                                    state: {
                                        backToDestination: {
                                            ...currentLocation,
                                            route: location.pathname,
                                        },
                                    },
                                },
                            );
                        }}
                    />
                </>
            }
        />
    );
};
